import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { auth } from '@/lib/supabase';
import { Button, Input, Card, Select } from '@/components/ui';

const registerSchema = z.object({
  firstName: z.string().min(2, 'Nome richiesto'),
  lastName: z.string().min(2, 'Cognome richiesto'),
  email: z.string().email('Email non valida'),
  password: z.string().min(8, 'La password deve avere almeno 8 caratteri'),
  confirmPassword: z.string(),
  licenseNumber: z.string().optional(),
  specialization: z.string().optional(),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'Devi accettare i termini e le condizioni',
  }),
  acceptPrivacy: z.boolean().refine((val) => val === true, {
    message: 'Devi accettare l\'informativa sulla privacy',
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Le password non corrispondono',
  path: ['confirmPassword'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

const specializationOptions = [
  { value: 'clinical', label: 'Nutrizione Clinica' },
  { value: 'sports', label: 'Nutrizione Sportiva' },
  { value: 'pediatric', label: 'Nutrizione Pediatrica' },
  { value: 'geriatric', label: 'Nutrizione Geriatrica' },
  { value: 'oncology', label: 'Nutrizione Oncologica' },
  { value: 'eating_disorders', label: 'Disturbi Alimentari' },
  { value: 'general', label: 'Nutrizione Generale' },
];

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      acceptTerms: false,
      acceptPrivacy: false,
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const { error: signUpError } = await auth.signUp(data.email, data.password, {
        first_name: data.firstName,
        last_name: data.lastName,
        role: 'nutritionist',
        license_number: data.licenseNumber,
        specialization: data.specialization,
      });

      if (signUpError) {
        if (signUpError.message.includes('already registered')) {
          setError('Questa email è già registrata');
        } else {
          setError('Errore durante la registrazione');
        }
        return;
      }

      setSuccess(true);
    } catch {
      setError('Errore durante la registrazione');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card padding="lg" className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Registrazione completata!</h2>
          <p className="text-gray-500 mb-6">
            Ti abbiamo inviato un'email di conferma. Clicca sul link per attivare il tuo account.
          </p>
          <Button onClick={() => navigate('/login')}>Vai al login</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-600 rounded-2xl mb-4">
            <span className="text-white font-bold text-2xl">N</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">NUTRY</h1>
          <p className="text-gray-500 mt-1">Crea il tuo account professionale</p>
        </div>

        <Card padding="lg">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">{error}</div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Nome"
                placeholder="Mario"
                error={errors.firstName?.message}
                {...register('firstName')}
              />
              <Input
                label="Cognome"
                placeholder="Rossi"
                error={errors.lastName?.message}
                {...register('lastName')}
              />
            </div>

            <Input
              label="Email"
              type="email"
              placeholder="nome@esempio.it"
              error={errors.email?.message}
              {...register('email')}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Password"
                type="password"
                placeholder="••••••••"
                error={errors.password?.message}
                {...register('password')}
              />
              <Input
                label="Conferma Password"
                type="password"
                placeholder="••••••••"
                error={errors.confirmPassword?.message}
                {...register('confirmPassword')}
              />
            </div>

            <Input
              label="Numero Iscrizione Ordine"
              placeholder="AA_A000000"
              hint="Numero iscrizione all'Ordine dei Biologi"
              error={errors.licenseNumber?.message}
              {...register('licenseNumber')}
            />

            <Select
              label="Specializzazione"
              options={specializationOptions}
              placeholder="Seleziona specializzazione"
              {...register('specialization')}
            />

            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  {...register('acceptTerms')}
                />
                <span className="text-sm text-gray-600">
                  Accetto i{' '}
                  <a href="/terms" className="text-teal-600 hover:underline">
                    Termini e Condizioni
                  </a>
                </span>
              </label>
              {errors.acceptTerms && (
                <p className="text-sm text-red-600">{errors.acceptTerms.message}</p>
              )}

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  {...register('acceptPrivacy')}
                />
                <span className="text-sm text-gray-600">
                  Accetto l'{' '}
                  <a href="/privacy" className="text-teal-600 hover:underline">
                    Informativa sulla Privacy
                  </a>{' '}
                  e il trattamento dei dati secondo il GDPR
                </span>
              </label>
              {errors.acceptPrivacy && (
                <p className="text-sm text-red-600">{errors.acceptPrivacy.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" isLoading={isLoading}>
              Crea account
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-gray-600">Hai già un account?</span>{' '}
            <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
              Accedi
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};
